import { imgSrc } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowRight, Camera, Check, Loader2, Trash2, Upload } from "lucide-react";

const FilledSlotTile = ({ slot, photo, busy, required, onReplace, onRemove }) => (
  <div data-testid={`onboarding-slot-filled-${slot.key}`} className="group relative overflow-hidden rounded-xl border">
    <img src={imgSrc(photo)} alt={slot.label} className="aspect-[3/4] w-full object-cover" />
    <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 pt-6 text-[10px] font-semibold uppercase tracking-wide text-white">
      {slot.label}
    </span>
    {required && (
      <span className="absolute left-1.5 top-1.5 rounded-full bg-[hsl(174,38%,34%)] p-1 text-white">
        <Check className="h-3 w-3" />
      </span>
    )}
    <div className="absolute right-1.5 top-1.5 flex gap-1 opacity-0 transition-[opacity] group-hover:opacity-100">
      {required && (
        <button type="button" title="Replace" data-testid={`onboarding-slot-replace-${slot.key}`}
          onClick={onReplace}
          className="rounded-full bg-white/90 p-1.5 text-foreground hover:bg-white">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
        </button>
      )}
      <button type="button" title="Remove" data-testid={`onboarding-slot-remove-${slot.key}`}
        onClick={onRemove}
        className="rounded-full bg-white/90 p-1.5 text-destructive hover:bg-white">
        <Trash2 className="h-3 w-3" />
      </button>
    </div>
  </div>
);

const EmptySlotTile = ({ slot, busy, required, onPick }) => (
  <button type="button" data-testid={`onboarding-slot-empty-${slot.key}`}
    onClick={onPick} disabled={busy}
    className={required
      ? "flex aspect-[3/4] flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed p-2 text-center text-muted-foreground transition-[border-color,color] hover:border-[hsl(174,38%,34%)] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      : "flex aspect-[3/4] flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed p-2 text-center text-muted-foreground/70 transition-[border-color,color] hover:border-[hsl(174,38%,34%)] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"}>
    {busy
      ? <Loader2 className={required ? "h-5 w-5 animate-spin" : "h-4 w-4 animate-spin"} />
      : <Upload className={required ? "h-5 w-5" : "h-4 w-4"} />}
    <span className={required ? "text-[11px] font-semibold leading-tight" : "text-[10px] font-medium"}>{slot.label}</span>
    {required && <span className="text-[9px] leading-tight opacity-70">{slot.hint}</span>}
  </button>
);

export const DatasetStep = ({
  checklist, requiredSlots, extraSlots, photosBySlot, uploadingSlot, facePhotoCount,
  slotFileRef, onPickSlot, onSlotFileChange, onRemovePhoto, onContinue,
}) => (
  <Card data-testid="onboarding-dataset-step" className="mt-6 rounded-2xl">
    <CardHeader className="pb-3">
      <CardTitle className="flex flex-wrap items-center justify-between gap-2 text-base">
        <span className="flex items-center gap-2"><Camera className="h-4 w-4" /> Standardized photo dataset</span>
        <span data-testid="onboarding-dataset-count" className="font-mono-plex text-xs text-muted-foreground">
          {checklist.dataset_required_filled}/{checklist.dataset_required_total} required
        </span>
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-5">
      <p className="text-sm text-muted-foreground">
        Only <span className="font-semibold text-foreground">3 photos are required</span> — front, side
        profile, and full body — shot in <span className="font-semibold text-foreground">even lighting</span> on
        a plain background with form-fitting clothing. These are the AI's core references for your digital twin.
        Add optional shots below anytime for an even sharper twin. JPEG, PNG or WebP, up to 8MB each.
      </p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {requiredSlots.map((s) => {
          const photo = photosBySlot[s.key];
          const busy = uploadingSlot === s.key;
          return photo ? (
            <FilledSlotTile key={s.key} slot={s} photo={photo} busy={busy} required
              onReplace={() => onPickSlot(s.key)} onRemove={() => onRemovePhoto(photo)} />
          ) : (
            <EmptySlotTile key={s.key} slot={s} busy={busy} required onPick={() => onPickSlot(s.key)} />
          );
        })}
      </div>

      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Optional shots — sharpen your twin's accuracy</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {extraSlots.map((s) => {
            const photo = photosBySlot[s.key];
            const busy = uploadingSlot === s.key;
            return photo ? (
              <FilledSlotTile key={s.key} slot={s} photo={photo} busy={busy} required={false}
                onRemove={() => onRemovePhoto(photo)} />
            ) : (
              <EmptySlotTile key={s.key} slot={s} busy={busy} required={false} onPick={() => onPickSlot(s.key)} />
            );
          })}
        </div>
      </div>

      <input ref={slotFileRef} data-testid="onboarding-slot-file-input" type="file" accept="image/jpeg,image/png,image/webp" hidden
        onChange={(e) => onSlotFileChange(e.target.files)} />

      <div className="flex items-center justify-between border-t pt-4">
        <p className="text-[11px] text-muted-foreground">
          Stored in secure cloud object storage. Your dataset powers accurate AI imagery — it is licensed per booking, never sold.
        </p>
        <Button data-testid="onboarding-dataset-continue-button" className="rounded-xl" disabled={facePhotoCount < 1}
          onClick={onContinue}>
          Continue <ArrowRight className="ml-1.5 h-4 w-4" />
        </Button>
      </div>
    </CardContent>
  </Card>
);
